<template>
  <div
    :name="schema.model"
    :id="schema.model"
    :data-tooltip="schema.attributes.title"
    :data-behavior="schema.typeCard"
    :class="[
      getSelectedDEV && 'dev-selected-component',
      'card',
      'd-flex',
      'elevation-2',
      `animation-${schema.attributes.animationDirection}`,
      { borderColor: schema.attributes.borderBottom },
      { 'card-hover': schema.typeCard === 'card-hover' },
      { 'card-vertical': schema.typeCard === 'card-vertical' },
      { 'card-horizontal': schema.typeCard === 'card-horizontal' }
    ]"
    @contextmenu="selectComponentDEV($event, schema.model)"
  >
    <h2
      v-if="schema.header"
      :class="['text-h6 pa-3 ma-0 font-weight-medium', schema.header.colorClass]"
      :style="background"
      color="#FFF"
    >
      <v-icon :color="schema.header.colorIcon" v-if="schema.header.icon" class="mr-1">{{ schema.header.icon }}</v-icon>
      {{ schema.header.label }}
    </h2>

    <div v-if="!schema.attributes.icon" class="image d-flex">
      <img :src="image" :title="schema.attributes.title" />
    </div>

    <div class="content-color"></div>

    <div
      :class="['content d-flex', schema.attributes.textAlign && `text-${schema.attributes.textAlign}`]"
      :style="schema.attributes.style"
    >
      <h2
        class="position-relative"
        :class="schema.attributes.titleClass"
        :style="schema.attributes.titleStyle"
        v-show="isVisibleTitle"
      >
        {{ schema.attributes.title }}

        <img 
          class="position-absolute" 
          v-if="schema.attributes.icon" 
          :src="image" 
          :title="schema.attributes.title"
          :style="schema.attributes.iconStyle"
        />
      </h2>

      <p :class="schema.attributes.descriptionClass" :style="schema.attributes.descriptionStyle">
        {{ schema.attributes.description }}
      </p>

      <div class="d-flex justify-space-around flex-wrap">
        <v-btn
          @click="execute(action)"
          v-bind="action.attributes"
          :disabled="action.readonly"
          v-for="(action, i) in schema.actions.items"
          :key="i"
        >
          <v-icon
            v-if="action.attributesIcon.icon && !action.attributesIcon.right"
            v-bind="action.attributesIcon"
            class="mr-1"
          >
            {{ action.attributesIcon.icon }}
          </v-icon>
          {{ action.label }}
          <v-icon
            v-if="action.attributesIcon.icon && action.attributesIcon.right"
            v-bind="action.attributesIcon"
            class="ml-1"
          >
            {{ action.attributesIcon.icon }}
          </v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { abstractField } from 'vue-form-generator';
import devComponentMixin from '@/mixins/dev-component-mixin';

import store from '@/store';
import * as HANDLERS from '@/helpers/types/handlers';

export default {
  mixins: [abstractField, devComponentMixin],

  data: () => ({
    background: '',
    actionName: ''
  }),

  mounted() {
    if (this.schema.header && !this.schema.header.colorClass && this.schema.header.colorHexa) {
      this.background = `background-color: ${this.schema.header.colorHexa}`;
    }
  },

  computed: {
    paths() {
      return this.$store.getters['layout/getConfigPaths'];
    },

    image() {
      let config = store.getters['authentication/getConfig'];

      if (config.DEV_MODULE) {
        return 'static/placeholder2.jpg';
      }

      return this.setPaths(this.schema.attributes.image);
    },
    isVisibleTitle() {
      if(this.schema.attributes.isVisibleTitle === undefined) { 
        return true
      }

      if(this.schema.attributes.isVisibleTitle) {
        return this.schema.attributes.isVisibleTitle
      }
    }
  },

  methods: {
    execute(action) {
      action.mode = this.formOptions.mode;

      this.$emit(HANDLERS.EXECUTE_ACTION, { action, origin: this.formOptions.page });
    },

    setPaths(path) {
      return path.startsWith('static') ? path : this.paths[path];
    }
  }
};
</script>

<style lang="scss" scoped>
.borderColor {
  border-bottom: 3px solid var(--v-primary-base) !important;
}

.card {
  h2.text-h6 {
    height: 50px;
    width: 100%;
    color: #fff;
    line-height: 27px;
  }

  .content {
    flex-direction: column;
    justify-content: space-around;

    h2 {
      font-size: 18px;
      font-weight: 300;
      margin: 15px 0;
    }
  }

  &.card-vertical {
    flex-direction: column;
    padding: 3px;
    border: 1px solid #e5e5e5;
    border-radius: 3px;

    .image {
      overflow: hidden;

      img {
        max-width: 100%;
        transition: all 0.3s;
      }

      &:hover {
        img {
          transform: scale(1.1);
        }
      }
    }

    .content {
      padding: 0 10px 15px 10px;
    }
  }

  &.card-horizontal {
    flex-wrap: wrap;

    .image {
      max-width: 50%;
      overflow: hidden;

      img {
        max-width: 100%;
        max-height: 432px;
        display: flex;
        transition: all 0.3s;
      }

      &:hover {
        img {
          transform: scale(1.1);
        }
      }
    }

    .content {
      width: 50%;
      text-align: center;
      padding: 10px 20px;

      h2 {
        margin-top: 25px;
        font-size: 22px;
      }
    }
  }

  &.card-hover {
    flex-direction: column;
    position: relative;
    border: none;
    padding: 0;
    overflow: hidden;

    .image {
      width: 100%;

      img {
        max-width: 100%;
      }
    }

    .content,
    .content-color {
      position: absolute;
      height: 100%;
      width: 100%;
      text-align: center;

      color: #fff;
      padding: 0 30px;

      z-index: 2;
    }

    .content-color {
      z-index: 1;
      opacity: 0.85;
      background: var(--v-primary-base);
    }
  }
}

.theme--dark {
  .card {
    &.card-vertical,
    &.card-horizontal {
      .content {
        background: var(--v-secondary-lighten1);

        h2 {
          color: #fff;
        }
      }
    }
  }
}

.theme--light {
  .card {
    &.card-vertical,
    &.card-horizontal {
      .content {
        background: var(--v-accent-lighten5);
      }
    }
  }
}

@media (max-width: 768px) {
  .card {
    &.card-hover {
      .content,
      .content-color {
        display: block;
        padding: 0 5px;

        h2 {
          font-size: 16px;
          margin: 10px 0 10px 0;
        }

        p {
          font-size: 14px;
        }
      }
    }
  }
}

@media (max-width: 425px) {
  .card {
    &.card-horizontal {
      flex-direction: column;
      padding: 3px;
      border: 1px solid #e5e5e5;
      border-radius: 3px;

      .image {
        overflow: hidden;
        max-width: 100%;

        img {
          max-width: 100%;
          -moz-transition: all 0.3s;
          -webkit-transition: all 0.3s;
          transition: all 0.3s;
        }

        &:hover {
          img {
            transform: scale(1.1);
          }
        }
      }

      .content {
        width: 100%;
        padding: 0 10px 15px 10px;
      }
    }
  }
}

</style>
